import { AirlineModel } from 'api-hooks/airline';
import { EmployeeLiteModel } from 'api-hooks/employee';
import { PreferenceTagItemModel } from 'api-hooks/preference-tag';
import { Expose, Type } from 'class-transformer';
import {
  CommonModel,
  CommonParamInput,
  ImageClass,
} from 'common/repositories/common.model';

// Enum

export enum GuestTypeEnum {
  individual = 'individual',
  corporate = 'corporate',
}

// Model

export class GuestListModel {
  id: string;
  text: string;
}

export class GuestPreferenceStatModel {
  id: string;
  name: string;

  @Type(() => Number)
  count: number;
}

export class GuestAirlineModel {
  id: string;
  note: string;
  password: string;

  @Type(() => AirlineModel)
  airline: AirlineModel;

  @Expose({ name: 'member_number' })
  memberNumber: string;
}

export class GuestPassportModel {
  id: string;
  type: string;
  number: string;
  nationality: string;
  gender: string;

  @Expose({ name: 'identifier_number' })
  identifierNumber: string | null;

  @Expose({ name: 'family_name' })
  familyName?: string | null;

  @Expose({ name: 'given_name' })
  givenName: string;

  @Expose({ name: 'issue_state' })
  issueState: string;

  @Expose({ name: 'place_of_birth' })
  placeOfBirth: string;

  @Expose({ name: 'date_of_birth' })
  @Type(() => Date)
  dateOfBirth: Date | null;

  @Expose({ name: 'expired_at' })
  @Type(() => Date)
  expiredAt: Date | null;

  @Type(() => ImageClass)
  files: ImageClass[];
}

export class GuestApecCardModel {
  id: string;
  type: string;
  number: string;
  name: string;
  email: string;
  pin: string;

  @Expose({ name: 'application_number' })
  applicationNumber: string;

  @Expose({ name: 'passport_number' })
  passportNumber: string;

  @Expose({ name: 'expired_at' })
  @Type(() => Date)
  expiredAt: Date | null;

  @Type(() => ImageClass)
  files: ImageClass[];
}

export class GuestVisaDocumentModel extends CommonModel {
  code: string | null;
  description: string;
  name: string;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Type(() => Number)
  price: number;
}

export class GuestVisaModel {
  id: string;
  number: string;

  @Expose({ name: 'visa_document' })
  @Type(() => GuestVisaDocumentModel)
  visaDocument: GuestVisaDocumentModel;

  @Expose({ name: 'issued_at' })
  @Type(() => Date)
  issuedAt: Date;

  @Expose({ name: 'expired_at' })
  @Type(() => Date)
  expiredAt: Date | null;

  @Type(() => ImageClass)
  files: ImageClass[];
}

export class GuestInsuranceModel {
  id: string;
  number: string;
  type: string;

  @Expose({ name: 'issued_at' })
  @Type(() => Date)
  issuedAt: Date;

  @Expose({ name: 'expired_at' })
  @Type(() => Date)
  expiredAt: Date | null;

  @Type(() => ImageClass)
  files: ImageClass[];
}

export class GuestVacineModel {
  id: string;
  @Type(() => ImageClass)
  files: ImageClass[];
}

export class GuestOtherModel {
  id: string;
  name: string;
  description: string;

  @Type(() => ImageClass)
  files: ImageClass[];
}

export class GuestRelationModel {
  id: string;
  type: string;
  name: string;
}

export class GuestCustomerRelationModel {
  name: string;

  @Expose({ name: 'no_of_sales' })
  @Type(() => Number)
  noOfSales: number;
}

export class GuestLiteModel extends CommonModel {
  name: string;
  title: string | null;
  gender: string;
  category: string;
  email: string;
  phone: string;
  phone_2?: string | null;
  alias?: string | null;

  @Expose({ name: 'created_by' })
  @Type(() => EmployeeLiteModel)
  createdBy: EmployeeLiteModel;

  @Expose({ name: 'preference_stats' })
  @Type(() => GuestPreferenceStatModel)
  preferenceStats: GuestPreferenceStatModel[];

  @Type(() => PreferenceTagItemModel)
  preferences: PreferenceTagItemModel[];
}

export class GuestModel extends GuestLiteModel {
  description: string;

  @Expose({ name: 'id_number' })
  idNumber: string;

  @Expose({ name: 'birth_place' })
  birthPlace: string;

  @Expose({ name: 'titled_name' })
  titledName: string;

  @Expose({ name: 'birth_date' })
  @Type(() => Date)
  birthDate: Date | null;

  @Type(() => GuestPassportModel)
  passports: GuestPassportModel;

  @Type(() => GuestAirlineModel)
  airlines: GuestAirlineModel[];

  @Type(() => GuestRelationModel)
  relations: GuestRelationModel[];

  @Type(() => GuestVisaModel)
  visas: GuestVisaModel[];

  @Expose({ name: 'apec_card' })
  @Type(() => GuestApecCardModel)
  apecCard: GuestApecCardModel;

  @Type(() => GuestInsuranceModel)
  insurances: GuestInsuranceModel[];

  @Type(() => GuestVacineModel)
  vaccines: GuestVacineModel[];

  @Type(() => GuestOtherModel)
  others: GuestOtherModel[];

  @Expose({ name: 'has_passport' })
  @Type(() => Boolean)
  hasPassport: boolean;

  @Expose({ name: 'has_apec_card' })
  @Type(() => Boolean)
  hasApecCard: boolean;
}

export class GuestGetSaleTotalModel {
  label: string;

  @Type(() => Number)
  x: number;

  @Type(() => Number)
  y: number;
}

export class TopFrequencyModel {
  type: string;

  @Type(() => Number)
  rank: number;

  @Type(() => Number)
  frequency: number;
}

export class TopModel {
  @Type(() => TopFrequencyModel)
  airlines: TopFrequencyModel[];

  @Type(() => TopFrequencyModel)
  hotels: TopFrequencyModel[];

  @Type(() => TopFrequencyModel)
  cruises: TopFrequencyModel[];

  @Expose({ name: 'travel_documents' })
  @Type(() => TopFrequencyModel)
  travelDocuments: TopFrequencyModel[];

  @Type(() => TopFrequencyModel)
  activities: TopFrequencyModel[];

  @Type(() => TopFrequencyModel)
  products: TopFrequencyModel[];
}

export class UpcomingModel {
  id: string;
  number: string;
  type: string;
  description: string;
  details: string;

  @Type(() => Date)
  date: Date;
}

export class LastTransactionModel {
  type: string;
  description: string;

  @Expose({ name: 'invoice_date' })
  @Type(() => Date)
  invoiceDate: Date;

  @Expose({ name: 'invoice_no' })
  invoiceNo: string;
}

export class TopBookingModel {
  name: string;

  @Expose({ name: 'sub_type' })
  subType: string;

  @Type(() => Number)
  count: number;
}

// Input

export type getGuestsInput = {
  params: CommonParamInput & {
    guest_id?: string;
  };
};

export type getGuestInput = {
  id: string;
  month_limit?: number;
};

export type getGuestCustomersInput = {
  id: string;
  params: {
    page: number;
    limit?: number;
    filter?: string;
    sort?: string;
    q?: string;
  };
};

export type GuestRelationItemInput = {
  id?: string;
  type: string | null;
  guestId: string | null;
};

export type GuestPassportInput = {
  id?: string | null;
  type: string | null;
  number: string | null;
  identifierNumber: string | null;
  familyName: string | null;
  givenName: string | null;
  issueState: string | null;
  placeOfBirth: string | null;
  nationality: string | null;
  gender: string | null;
  dateOfBirth: Date | null;
  expiredAt: Date | null;
  files: { id?: string; file: string }[];
};

export type GuestApecCardInput = {
  id?: string | null;
  type: string | null;
  number: string | null;
  name: string | null;
  applicationNumber: string | null;
  email: string | null;
  passportNumber: string | null;
  pin: string | null;
  expiredAt: Date | null;
  files: { id?: string; file: string }[];
};

export type GuestAirlineItemInput = {
  id?: string;
  airlineId: string;
  memberNumber: string;
  password: string;
  note: string;
};

export type GuestVisaItemInput = {
  id?: string;
  visaDocumentId: string;
  number: string;
  issuedAt: Date | null;
  expiredAt: Date | null;
  files: { id?: string; file: string }[];
};

export type GuestInsuranceItemInput = {
  id?: string;
  type: string | null;
  number: string;
  issuedAt: Date | null;
  expiredAt: Date | null;
  files: { id?: string; file: string }[];
};

export type GuestVacineItemInput = {
  id?: string;
  files: { id?: string; file: string }[];
};

export type GuestOtherItemInput = {
  id?: string;
  name: string;
  description: string;
  files: { id?: string; file: string }[];
};

export type GuestMutationInput = {
  // REQUIRED
  title: string | null;
  name: string;
  alias: string;
  gender: string;
  email: string;
  category: string;
  birthPlace: string;
  birthDate: Date | null;
  idNumber: string;
  phone: string;
  relations: GuestRelationItemInput[];
  airlines: GuestAirlineItemInput[];
  visas: GuestVisaItemInput[];
  vaccines: GuestVacineItemInput[];
  others: GuestOtherItemInput[];
  insurances: GuestInsuranceItemInput[];
  hasPassport: boolean;
  hasApecCard: boolean;
  // NULLABLE
  phone_2: string;
  preferences: string[];
  passport: GuestPassportInput | null;
  apecCard: GuestApecCardInput | null;
};

export type GuestMassUpdatePassportMutation = {
  passports: GuestPassportInput[];
};

export type GuestUpdateInput = {
  guestId: string;
  body: GuestMutationInput;
};

export type GuestDeleteInput = {
  guestId: string;
};

export type GuestUpdateDescriptionInput = {
  guestId: string;
  body: {
    description: string;
  };
};
